import React, { useEffect, useState, useMemo, useCallback } from "react";
import CustomTable from "../../../components/common/CustomTable";
import { MdDone, MdClose, MdRemoveRedEye } from "react-icons/md";
import CustomSidebar from "../../../components/common/CustomSidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { userId, userUid } from "../../../utils/storage";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/Constant/Notification";
import { ToastContainer } from "react-toastify";
import Header from "../../Header/Header";
import CustomTitle from "../../../GeoLocationCompoents/Custom/CustomTitle";
import leaveRequestService from "../../Services/leaveRequestService";
import RequestFilter from "./RequestFilter";
import { MdFilterListAlt } from "react-icons/md";
import CustomSearch from "../../../components/common/CustomSearch";
import ConfirmationModal from "../../../components/shareableContainer/ConfirmationModal";
import { IoMdCloudDownload } from "react-icons/io";
import LeaveRequestForm from "./LeaveRequestForm";
import html2pdf from "html2pdf.js";
import teamService from "../../../components/Services/TeamService";
import CustomChip from "../../../components/common/CustomChip";
import { formatToIndianDate } from "../../../utils/dateFormatter";

const RequestList = () => {
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [requestData, setRequestData] = useState([]);
  const [nextCursor, setNextCursor] = useState("");
  const [previousCursor, setPreviousCursor] = useState("");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedRequestForAction, setSelectedRequestForAction] =
    useState(null);
  const [notes, setNotes] = useState("");
  const [selectedEmployeeForm, setSelectedEmployeeForm] = useState();
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedPermissionType, setSelectedPermissionType] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [cache, setCache] = useState(new Map());
  const [cursorHistory, setCursorHistory] = useState([""]);

  const tabs = [
    { label: "Pending", value: "PENDING" },
    { label: "Accepted", value: "APPROVED" },
    { label: "Rejected", value: "REJECTED" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const cacheKey = useMemo(() => {
    return `${activeTab}-${startDate?.getTime() || ""}-${
      endDate?.getTime() || ""
    }-${limit}-${debouncedSearchTerm}-${selectedPermissionType?.value || ""}-${
      selectedTeam?.value || ""
    }-${selectedUser?.value || ""}`;
  }, [
    activeTab,
    startDate,
    endDate,
    limit,
    debouncedSearchTerm,
    selectedPermissionType,
    selectedTeam,
    selectedUser,
  ]);

  const fetchData = useCallback(
    async (direction = "next", cursor = "", page = 1) => {
      const pageKey = `${cacheKey}-${page}`;
      const cachedData = cache.get(pageKey);

      if (cachedData) {
        setRequestData(cachedData.data);
        setNextCursor(cachedData.nextCursor);
        setPreviousCursor(cachedData.previousCursor);
        setHasMore(cachedData.hasMore);
        return;
      }

      setLoading(true);
      try {
        const body = {
          statusType: activeTab.toUpperCase(),
          next: direction === "next" ? cursor : "",
          limit,
          direction,
          userId: selectedUser?.value || null,
          searchKey: debouncedSearchTerm || null,
          teamId: selectedTeam?.value || null,
          startDate: startDate ? formatToIndianDate(startDate) : null,
          endDate: endDate ? formatToIndianDate(endDate) : null,
          leaveType: selectedPermissionType?.value || null,
        };

        const response = await leaveRequestService.getLeaveRequestList(body);

        if (response && response.data) {
          const mappedData = response.data.leaveRequests || [];
          const newNextCursor = response.data.next || "";
          const newPreviousCursor = response.data.previous || "";
          const newHasMore = mappedData.length === limit;

          setCache((prevCache) => {
            const newCache = new Map(prevCache);
            newCache.set(pageKey, {
              data: mappedData,
              nextCursor: newNextCursor,
              previousCursor: newPreviousCursor,
              hasMore: newHasMore,
            });
            return newCache;
          });

          setRequestData(mappedData);
          setNextCursor(newNextCursor);
          setPreviousCursor(newPreviousCursor);
          setHasMore(newHasMore);
        }
      } catch (error) {
        console.error("Error fetching leave request list:", error);
        showErrorToast("Failed to fetch leave requests");
      } finally {
        setLoading(false);
      }
    },
    [
      cacheKey,
      limit,
      activeTab,
      debouncedSearchTerm,
      selectedTeam,
      startDate,
      endDate,
      selectedPermissionType,
    ]
  );

  useEffect(() => {
    setCurrentPage(1);
    setCursorHistory([""]);
    setCache(new Map());
    fetchData("next", "", 1);
  }, [fetchData, limit]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await teamService.getTeamName();
        const data = response.data?.teams || [];
        setTeams(
          data.map((team) => ({ label: team.name, value: team.teamId }))
        );
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchTeams();
  }, []);

  const handleClearAndApplyFilter = useCallback(() => {
    setSelectedPermissionType(null);
    setSelectedTeam(null);
    setSelectedUser(null);
    setStartDate(null);
    setEndDate(null);
    setSearchInput("");
    setDebouncedSearchTerm("");
  }, []);

  const handleRemoveChip = useCallback((chipType) => {
    switch (chipType) {
      case "permissionType":
        setSelectedPermissionType(null);
        break;
      case "team":
        setSelectedTeam(null);
        setSelectedUser(null);
        break;
      case "user":
        setSelectedUser(null);
        break;
      case "dateRange":
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        break;
    }
  }, []);

  const handleSearch = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setDebouncedSearchTerm(searchInput);
    },
    [searchInput]
  );

  const handlePageChange = (token, direction) => {
    if (direction === "next") {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setCursorHistory((prev) => {
        const newHistory = [...prev];
        if (newHistory.length <= nextPage - 1) {
          newHistory.push(nextCursor);
        }
        return newHistory;
      });
      fetchData("next", nextCursor, nextPage);
    } else if (direction === "previous") {
      const prevPage = Math.max(currentPage - 1, 1);
      setCurrentPage(prevPage);
      const prevCursor = cursorHistory[prevPage - 1] || "";
      fetchData("previous", prevCursor, prevPage);
    }
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
  };

  const handleAction = async (leaveRequestId, action) => {
    const updatedData = requestData.map((item) => {
      if (item.id === leaveRequestId) {
        return { ...item, permission: action.toUpperCase() };
      }
      return item;
    });

    setRequestData(updatedData);

    const pageKey = `${cacheKey}-${currentPage}`;
    const cachedData = cache.get(pageKey);
    if (cachedData) {
      const updatedCacheData = cachedData.data.map((item) => {
        if (item.id === leaveRequestId) {
          return { ...item, permission: action.toUpperCase() };
        }
        return item;
      });

      setCache((prevCache) => {
        const newCache = new Map(prevCache);
        newCache.set(pageKey, {
          ...cachedData,
          data: updatedCacheData,
        });
        return newCache;
      });
    }

    try {
      const body = {
        leaveRequestId,
        action: action.toUpperCase(),
        actionBy: userUid,
        ...(action.toUpperCase() === "REJECTED" && { rejectionReason: notes }),
      };

      const response = await leaveRequestService.actionApproveLeaveRequest(
        body
      );

      if (response?.status === 200) {
        showSuccessToast(
          response.data.message || "Action completed successfully"
        );

        if (activeTab !== action.toUpperCase()) {
          setTimeout(() => {
            setRequestData((prevData) =>
              prevData.filter((item) => item.id !== leaveRequestId)
            );

            if (cachedData) {
              setCache((prevCache) => {
                const newCache = new Map(prevCache);
                newCache.set(pageKey, {
                  ...cachedData,
                  data: cachedData.data.filter(
                    (item) => item.id !== leaveRequestId
                  ),
                });
                return newCache;
              });
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error(`Error performing ${action} on leave request:`, error);
      showErrorToast(
        error.response?.data?.message || `Failed to ${action} request`
      );

      setRequestData((prevData) => {
        return prevData.map((item) => {
          if (item.id === leaveRequestId) {
            return {
              ...item,
              permission: selectedRequestForAction?.permission || "PENDING",
            };
          }
          return item;
        });
      });

      if (cachedData) {
        setCache((prevCache) => {
          const newCache = new Map(prevCache);
          newCache.set(pageKey, cachedData);
          return newCache;
        });
      }
    }
  };

  const showConfirmation = (rowData, action) => {
    setSelectedRequestForAction(rowData);
    setPendingAction(action);
    setShowConfirmModal(true);
    setNotes("");
  };

  const executeConfirmedAction = () => {
    if (selectedRequestForAction && pendingAction) {
      handleAction(selectedRequestForAction.id, pendingAction);
    }
    setShowConfirmModal(false);
    setPendingAction(null);
    setSelectedRequestForAction(null);
    setNotes("");
  };

  const cancelConfirmation = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
    setSelectedRequestForAction(null);
    setNotes("");
  };

  const handleAccept = (rowData) => {
    showConfirmation(rowData, "APPROVED");
  };

  const handleReject = (rowData) => {
    showConfirmation(rowData, "REJECTED");
  };

  const handleToggleToReject = (rowData) => {
    showConfirmation(rowData, "REJECTED");
  };

  const handleToggleToAccept = (rowData) => {
    showConfirmation(rowData, "APPROVED");
  };

  const handleViewDetails = (rowData) => {
    const updatedRequest = requestData.find((item) => item.id === rowData.id);
    setSelectedRequest(updatedRequest);
    setSidebarVisible(true);
  };

  const generateLeaveRequestPDF = async (leaveData) => {
    setIsGeneratingPDF(true);

    try {
      const element = document.getElementById("leave-form-content");

      if (!element) {
        console.error("PDF content element not found");
        return;
      }

      const originalDisplay = element.style.display;
      element.style.display = "block";

      const today = new Date();
      const formattedDate = `${today.getDate().toString().padStart(2, "0")}-${(
        today.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${today.getFullYear()}`;

      const options = {
        filename: `${formattedDate}_${leaveData.name}_${leaveData.leaveType}_Request.pdf`,
        image: { type: "png", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          margin: [10, 10, 10, 10],
        },
      };

      await html2pdf().from(element).set(options).save();

      element.style.display = originalDisplay;

      showSuccessToast("Leave request form downloaded successfully!");
      setShowLeaveForm(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      showErrorToast("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = (rowData) => {
    setSelectedEmployeeForm(rowData);
    setShowLeaveForm(true);

    setTimeout(() => {
      generateLeaveRequestPDF(rowData);
    }, 100);
  };

  const modeOfCommunicationOptions = [
    {
      label:"Email Message",
      value:"EMAIL"
    },
    {
      label:"Phone Call",
      value:"PHONE"
    },
    {
      label:"In Person",
      value:"IN_PERSON"
    },
    {
      value:"CALL",
      label:"Call"
    },
    {
      value:"TEXT",
      label:"Text Message"
    },
    {
      value:"VOICE",
      label:"Voice Message"
    }
  ]

  const columns = [
    {
      key: "name",
      title: "User Name",
      render: (value, row) => (
        <div className="text-center">
          <div className="font-medium">{value}</div>
          <div className="text-sm text-customGreen">Emp ID: {row.employeeId}</div>
        </div>
      ),
    },
    { key: "teamNames", title: "Team", render: (value) => value.join(", ") || "No Team" },
    { key: "appliedDate", title: "Applied Date" },
    {
      key: "leaveType",
      title: "Type",
      render: (value) => (
        <div className="capitalize">{value?.toLowerCase()}</div>
      )
        
      
    },
    { key: "leaveDate", title: "Leave Date" ,
      render: (_, record)=>(
        <div>{record.leaveType === "LEAVE" ? `${record.startDate} TO ${record.endDate}` : record.startDate}</div>

      )
     },
    {
      key: "isApproved",
      title: "Permission",
      render: (value, row) => {
        if (value === "PENDING") {
          return (
            <div className="flex gap-2 justify-center items-center">
              <button
                onClick={() => handleAccept(row)}
                className="flex items-center gap-1 text-green-500 px-3 py-1 rounded-full text-sm transition-colors"
              >
                <div className="p-[3px] rounded-full text-white bg-green-500">
                  <MdDone className="text-sm" />
                </div>
                Accept
              </button>
              <button
                onClick={() => handleReject(row)}
                className="flex items-center gap-1 text-red-500 px-3 py-1 rounded-full text-sm transition-colors"
              >
                <div className="p-[3px] rounded-full text-white bg-red-500">
                  <MdClose className="text-sm" />
                </div>
                Reject
              </button>
            </div>
          );
        } else if (value === "APPROVED" || value === "REJECTED") {
          const isAccepted = value === "APPROVED";
          return (
            <div className="flex justify-center items-center">
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm font-medium ${
                    !isAccepted ? "text-red-600" : "text-gray-400"
                  }`}
                >
                  Reject
                </span>
                <label className="inline-flex cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAccepted}
                    onChange={() =>
                      isAccepted
                        ? handleToggleToReject(row)
                        : handleToggleToAccept(row)
                    }
                    className="sr-only peer"
                  />
                  <div
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      isAccepted ? "bg-emerald-600" : "bg-red-500"
                    } after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                      isAccepted ? "after:translate-x-6" : ""
                    }`}
                  />
                </label>
                <span
                  className={`text-sm font-medium ${
                    isAccepted ? "text-emerald-600" : "text-gray-400"
                  }`}
                >
                  Accept
                </span>
              </div>
            </div>
          );
        }
      },
    },
    {
      key: "Action",
      title: "Action",
      render: (value, row) => (
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-500 hover:text-blue-700 transition-colors"
            title="View Details"
          >
            <MdRemoveRedEye className="text-xl" />
          </button>
          <button
            onClick={() => handleDownloadPDF(row)}
            className="text-green-500 hover:text-green-700 transition-colors"
            title="Download Leave Form"
            disabled={isGeneratingPDF}
          >
            <IoMdCloudDownload className="text-xl" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header />
      <ToastContainer />
      <div className="pt-28 px-6 pb-10">
        <div className="flex justify-between items-center mb-6">
          <CustomTitle
            title={"Leave Request"}
            textColor={"primary"}
            isIcon={true}
          />
          <div className="flex items-center gap-4">
            <CustomSearch
              placeholder="Search by Name"
              handleSearchChange={handleSearch}
              searchTerm={searchInput}
              handleSearchSubmit={handleSearchSubmit}
            />
            <button
              onClick={() => setIsFilterOpen(true)}
              className="bg-white justify-between text-base font-medium w-[131px] h-[50px] rounded-[10px] flex items-center gap-3 text-customGreen py-2 px-4 hover:bg-appGreenHover transition duration-300"
            >
              Filters
              <MdFilterListAlt />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedPermissionType && (
            <CustomChip
              value={selectedPermissionType.label}
              onRemove={() => handleRemoveChip("permissionType")}
            />
          )}
          {selectedTeam && (
            <CustomChip
              value={selectedTeam.label}
              onRemove={() => handleRemoveChip("team")}
            />
          )}
          {selectedUser && (
            <CustomChip
              value={selectedUser.label}
              onRemove={() => handleRemoveChip("user")}
            />
          )}
          {startDate && endDate && (
            <CustomChip
              value={`Start: ${formatToIndianDate(
                startDate
              )} - End: ${formatToIndianDate(endDate)}`}
              onRemove={() => handleRemoveChip("dateRange")}
            />
          )}
        </div>

        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(tab.value)}
                  className={`py-2 px-1 border-b-2 font-medium text-base whitespace-nowrap ${
                    activeTab === tab.value
                      ? "border-customGreen text-customGreen"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <CustomTable
          isPagination={true}
          data={requestData}
          columns={columns}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          pageSize={limit}
          hasMore={hasMore}
          loading={loading}
        />

<CustomSidebar
  title="Leave Request Details"
  visible={sidebarVisible}
  onHide={() => setSidebarVisible(false)}
  style={{ width: "400px" }}
  children={
    <div className="space-y-6">
      {/* Employee Name */}
      <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="employeeName">
          Employee Name
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="employeeName"
            value={selectedRequest?.name || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Employee ID */}
      <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="employeeId">
          Employee ID
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="employeeId"
            value={selectedRequest?.employeeId || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Team */}
      <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="team">
          Team
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="team"
            value={selectedRequest?.teamNames?.join(", ") || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Leave Type */}
      <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="leaveType">
          Leave Type
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="leaveType"
            value={selectedRequest?.leaveType?.replace(/_/g, " ") || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Date Fields based on Leave Type */}
      {selectedRequest?.leaveType === "LEAVE" && (
        <div className="grid grid-cols-12 items-center">
          <label className="col-span-4" htmlFor="dateRange">
            Date Range
          </label>
          <div className="col-span-8">
            <input
              type="text"
              id="dateRange"
              value={`${selectedRequest?.startDate || ""} - ${selectedRequest?.endDate || ""}`}
              readOnly
              className="border rounded text-sm w-full input-focus px-3 py-2"
            />
          </div>
        </div>
      )}

      {(selectedRequest?.leaveType === "PERMISSION" || selectedRequest?.leaveType === "HALFDAY") && (
        <>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4" htmlFor="date">
              Date
            </label>
            <div className="col-span-8">
              <input
                type="text"
                id="date"
                value={selectedRequest?.startDate || ""}
                readOnly
                className="border rounded text-sm w-full input-focus px-3 py-2"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4" htmlFor="startTime">
              Start Time
            </label>
            <div className="col-span-8">
              <input
                type="text"
                id="startTime"
                value={selectedRequest?.startTime || ""}
                readOnly
                className="border rounded text-sm w-full input-focus px-3 py-2"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4" htmlFor="endTime">
              End Time
            </label>
            <div className="col-span-8">
              <input
                type="text"
                id="endTime"

                value={selectedRequest?.endTime || ""}
                readOnly
                className="border rounded text-sm w-full input-focus px-3 py-2"
              />
            </div>
          </div>
        </>
      )}

      {/* Mode of Communication */}
      <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="modeOfCommunication">
          Mode of Communication
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="modeOfCommunication"
            value={modeOfCommunicationOptions.find((list)=> list?.value === selectedRequest?.modeOfCommunication)?.label || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Handover To */}
      {/* <div className="grid grid-cols-12 items-center">
        <label className="col-span-4" htmlFor="handoverTo">
          Handover To
        </label>
        <div className="col-span-8">
          <input
            type="text"
            id="handoverTo"
            value={selectedRequest?.handoverTo || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div> */}

      {/* Compensate Type */}
      {selectedRequest?.compensateType && (
        <div className="grid grid-cols-12 items-center">
          <label className="col-span-4" htmlFor="compensateType">
            Compensate Type
          </label>
          <div className="col-span-8">
            <input
              type="text"
              id="compensateType"
              value={selectedRequest?.compensateType || ""}
              readOnly
              className="border rounded text-sm w-full input-focus px-3 py-2"
            />
          </div>
        </div>
      )}

      {/* Compensate Date */}
      {selectedRequest?.compensateDate && (
        <div className="grid grid-cols-12 items-center">
          <label className="col-span-4" htmlFor="compensateDate">
            Compensate Date
          </label>
          <div className="col-span-8">
            <input
              type="text"
              id="compensateDate"
              value={selectedRequest?.compensateDate || ""}
              readOnly
              className="border rounded text-sm w-full input-focus px-3 py-2"
            />
          </div>
        </div>
      )}

      {/* Reason */}
      <div className="grid grid-cols-12">
        <label className="col-span-4" htmlFor="reason">
          Reason
        </label>
        <div className="col-span-8">
          <textarea
            id="reason"
            rows={5}
            value={selectedRequest?.reason || ""}
            readOnly
            className="border rounded text-sm w-full input-focus px-3 py-2"
          />
        </div>
      </div>

      {/* Status */}
      <div className="grid grid-cols-12">
        <label className="col-span-4" htmlFor="status">
          Status
        </label>
        <div className="col-span-8">
          {selectedRequest?.isApproved === "APPROVED" ? (
            <span className="text-green-500 font-semibold">Approved</span>
          ) : selectedRequest?.isApproved === "REJECTED" ? (
            <span className="text-red-500 font-semibold">Rejected</span>
          ) : (
            <span className="text-yellow-500 font-semibold">Pending</span>
          )}
        </div>
      </div>

      {/* Rejection Reason */}
      {selectedRequest?.rejectionReason && (
        <div className="grid grid-cols-12">
          <label className="col-span-4" htmlFor="rejectionReason">
            Rejection Reason
          </label>
          <div className="col-span-8">
            <textarea
              id="rejectionReason"
              rows={3}
              value={selectedRequest?.rejectionReason || ""}
              readOnly
              className="border rounded text-sm w-full input-focus px-3 py-2"
            />
          </div>
        </div>
      )}
    </div>
  }
/>
        <RequestFilter
          visible={isFilterOpen}
          onHide={() => setIsFilterOpen(false)}
          teams={teams}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          selectedPermissionType={selectedPermissionType}
          setSelectedPermissionType={setSelectedPermissionType}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          clearFilters={handleClearAndApplyFilter}
        />

        <ConfirmationModal
          showModal={showConfirmModal}
          title={"Confirm Action"}
          content={
            pendingAction === "REJECTED" ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Please provide a reason for rejection"
                className="w-full border input-focus border-gray-300 rounded-md shadow-sm p-2"
                rows={3}
              />
            ) : null
          }
          message={"Are you sure you want to proceed with this action?"}
          onConfirm={executeConfirmedAction}
          onCancel={cancelConfirmation}
        />

        {isGeneratingPDF && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white px-8 py-6 rounded-lg shadow-lg flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-customGreen mb-4"></div>
              <p className="text-gray-700 font-medium">Generating PDF...</p>
            </div>
          </div>
        )}
        {/* <LeaveRequestForm selectedEmployeeForm={selectedEmployeeForm} /> */}
        <div className="hidden">
        <div id="leave-form-content" style={{ display: "none" }}>
          {selectedEmployeeForm && (
            <LeaveRequestForm selectedEmployeeForm={selectedEmployeeForm} />
          )}
        </div>
        </div>
       
      </div>
    </div>
  );
};

export default RequestList;
